import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import Eyebrow from '@Guidelines/Component/Eyebrow';
import { theme } from '@Guidelines/Theme/theme';
import type InternalInterviewListEntryView from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList/InternalInterviewListEntryView';
import ElementRow from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/ElementsSection/ElementRow';
import LocalDraftRow from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/ElementsSection/LocalDraftRow';
import MissingSourceBanner from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/ElementsSection/MissingSourceBanner';
import useElementDescriber from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/Hooks/useElementDescriber';
import type WorkflowRunView from '@UI/Store/WorkflowRuns/Views/WorkflowRunView';
import { css } from '@emotion/react';
import type * as React from 'react';

const sectionStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const listBlockStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.375rem;
`;

const listStyle = css`
	display: flex;
	flex-direction: column;
	margin: 0;
	padding: 0;
	list-style: none;
`;

const emptyStyle = css`
	margin: 0;
	padding: 1rem 1.125rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
	font-family: ${theme.fonts.body};
	font-size: 0.78125rem;
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly preparation: WorkflowRunView;
	readonly drafts: readonly InternalInterviewListEntryView[];
	readonly entryByInterviewId: ReadonlyMap<number, InternalInterviewListEntryView>;
	/** Whether the visitor may write in this dossier (permission, dossier not archived). */
	readonly canWrite: boolean;
};

/**
 * What the preparation holds: the carried-over documents the dossier lacks, then one list of elements
 * (recordings still only on this device first). Once the generation is launched the list is closed:
 * read-only, no removal, no choosing another document.
 */
const ElementsSection: React.FC<Props> = ({ subject, preparation, drafts, entryByInterviewId, canWrite }) => {
	const { labelOfDocumentType } = useElementDescriber(subject);
	const canEdit = preparation.isOpen && canWrite;
	const count = preparation.sources.length + drafts.length;

	let countLabel = 'Aucun';
	if (count > 0) {
		countLabel = `${count} élément${count > 1 ? 's' : ''}`;
	}

	return (
		<div css={sectionStyle}>
			<ConditionalLoader data={preparation.isOpen}>
				{preparation.missingAutomaticSources.map((source) => (
					<MissingSourceBanner
						key={source.documentTypeId}
						documentTypeId={source.documentTypeId}
						typeLabel={labelOfDocumentType(source.documentTypeId)}
						verifiedOnly={source.verifiedOnly}
						canEdit={canWrite}
					/>
				))}
			</ConditionalLoader>
			<div css={listBlockStyle}>
				<Eyebrow>Éléments · {countLabel}</Eyebrow>
				<ConditionalLoader
					data={count > 0}
					fallback={
						<p css={emptyStyle}>
							Aucun élément pour l’instant. Ajoutez un entretien, une note ou un document du
							dossier.
						</p>
					}
				>
					<ul css={listStyle}>
						{drafts.map((entry) => (
							<LocalDraftRow
								key={entry.key}
								entry={entry}
								subject={subject}
								canEdit={canWrite}
							/>
						))}
						{preparation.sources.map((element) => (
							<ElementRow
								key={element.id}
								subject={subject}
								preparationId={preparation.id}
								element={element}
								canEdit={canEdit}
								canWrite={canWrite}
								localEntry={
									element.interviewId !== null
										? (entryByInterviewId.get(element.interviewId) ?? null)
										: null
								}
							/>
						))}
					</ul>
				</ConditionalLoader>
				<ConditionalLoader data={!preparation.isOpen}>
					<p css={emptyStyle}>Après le lancement, la préparation ne reçoit plus d’éléments.</p>
				</ConditionalLoader>
			</div>
		</div>
	);
};

export default ElementsSection;
